//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { type UserProfile } from '@/services/apiTypes';

/**
 * A setter for one preference held on the user's server profile: writes it
 * optimistically, then rolls back to what the server still holds if the
 * write is rejected. Preferences follow the person, so they belong here on
 * `UserProfile`; window and session layout stays in the browser instead,
 * which is `usePersistentState`'s side of that split.
 */
export function useProfilePreference<K extends keyof UserProfile>(
  key: K, fallback: NonNullable<UserProfile[K]>
) {
  const { userProfile, setUserProfile, updateUserProfile } = useAuth();

  return useCallback((next: NonNullable<UserProfile[K]>) => {
    const held = userProfile?.[key] ?? fallback;
    setUserProfile({ ...userProfile, [key]: next });
    // The key is generic, so TypeScript can't verify the computed property
    // against `UserProfile` the way it would a literal key; the value itself
    // is still checked, by the parameter above.
    updateUserProfile({ [key]: next } as Pick<UserProfile, K>).catch(() => {
      setUserProfile({ ...userProfile, [key]: held });
    });
  }, [key, fallback, userProfile, setUserProfile, updateUserProfile]);
}
